/* eslint-disable no-restricted-syntax -- The camera bodies stand in for product
   photography, so their greys and silvers must NOT follow the app theme or the
   merchant's derived palette. This is the third-party/fixed-artwork opt-out the
   rule describes. Every other surface in the preview is theme- or scheme-driven. */
import React from 'react';

/**
 * Product drawings for the storefront mock preview.
 *
 * Ported from the design handoff's `Camera.dc.html`: absolutely positioned
 * gradient boxes on a 200x130 canvas, scaled per slot.
 *
 * These stand in for product photography, so they are deliberately NEUTRAL —
 * black, silver and a little colour film. They are never tinted with the
 * merchant's derived scheme, because real product photos would not be either.
 * Everything else in the preview takes its colour from the scheme set.
 */

export type PresetCameraKind = 'mirrorless' | 'rangefinder' | 'compact' | 'instant' | 'lens';

/**
 * One absolutely positioned box on the 200x130 canvas.
 *
 * These are literal CSS, and they are rendered through `style`, never MUI's
 * `sx`. `sx` reads `borderRadius` as a multiple of `theme.shape.borderRadius`
 * (12 here), so a part asking for `borderRadius: 12` would come out at 144px
 * and every camera body would render as a pill. `sx` also reads a `width` or
 * `height` of 1 or less as a percentage. A drawing is fixed artwork — it must
 * not pass through the theme at all.
 */
type Part = React.CSSProperties;

const DRAWINGS: Record<PresetCameraKind, Part[]> = {
  mirrorless: [
    { left: 76, top: 6, width: 48, height: 26, borderRadius: '8px 8px 2px 2px', background: 'linear-gradient(#3a3a3a,#1e1e1e)' },
    { left: 32, top: 16, width: 18, height: 8, borderRadius: '3px', background: '#444' },
    { left: 150, top: 18, width: 16, height: 8, borderRadius: '3px', background: '#333' },
    { left: 18, top: 24, width: 164, height: 88, borderRadius: '12px', background: 'linear-gradient(#303030,#121212)', boxShadow: '0 10px 18px #0004' },
    { left: 18, top: 24, width: 38, height: 88, borderRadius: '12px 4px 4px 12px', background: 'repeating-linear-gradient(90deg,#141414 0 2px,#1b1b1b 2px 4px)' },
    { left: 62, top: 34, width: 76, height: 76, borderRadius: '50%', background: 'linear-gradient(#3b3b3b,#151515)', boxShadow: '0 4px 10px #0006' },
    { left: 70, top: 42, width: 60, height: 60, borderRadius: '50%', background: 'radial-gradient(circle at 40% 36%,#8fb0d0 0 5%,#223244 9% 28%,#06080b 32% 55%,#2a2a2a 57% 66%,#0d0d0d 68%)' },
  ],
  rangefinder: [
    { left: 34, top: 18, width: 14, height: 8, borderRadius: '2px', background: '#bdbdbd' },
    { left: 140, top: 14, width: 22, height: 12, borderRadius: '3px', background: 'linear-gradient(#d8d8d8,#9a9a9a)' },
    // Body is a two-band stack (silver top plate over textured leatherette).
    { left: 14, top: 24, width: 172, height: 84, borderRadius: '9px', background: 'linear-gradient(#f2f2f2 0 36%,#bfbfbf 36%)', boxShadow: '0 10px 18px #0004', overflow: 'hidden' },
    { left: 14, top: 54, width: 172, height: 54, background: 'repeating-linear-gradient(45deg,#1b1b1b 0 2px,#232323 2px 4px)' },
    { left: 24, top: 32, width: 28, height: 14, borderRadius: '2px', background: 'linear-gradient(135deg,#6d7c8a,#1a2129)' },
    { left: 62, top: 34, width: 12, height: 10, borderRadius: '2px', background: '#2a3038' },
    { left: 100, top: 46, width: 62, height: 62, borderRadius: '50%', background: 'linear-gradient(#e6e6e6,#8c8c8c)', boxShadow: '0 4px 10px #0005' },
    { left: 108, top: 54, width: 46, height: 46, borderRadius: '50%', background: 'radial-gradient(circle at 40% 36%,#9fbcd6 0 5%,#243446 9% 30%,#050709 34% 60%,#333 62%)' },
  ],
  compact: [
    { left: 130, top: 20, width: 20, height: 8, borderRadius: '3px', background: '#3a3a3a' },
    { left: 156, top: 22, width: 14, height: 6, borderRadius: '2px', background: '#555' },
    { left: 16, top: 26, width: 168, height: 82, borderRadius: '10px', background: 'linear-gradient(#2e2e2e,#101010)', boxShadow: '0 10px 18px #0004' },
    { left: 16, top: 26, width: 26, height: 82, borderRadius: '10px 3px 3px 10px', background: 'repeating-linear-gradient(0deg,#141414 0 2px,#1c1c1c 2px 4px)' },
    { left: 150, top: 34, width: 18, height: 10, borderRadius: '2px', background: '#c0392b' },
    { left: 70, top: 36, width: 66, height: 66, borderRadius: '50%', background: 'linear-gradient(#444,#1a1a1a)', boxShadow: '0 4px 10px #0006' },
    { left: 79, top: 45, width: 48, height: 48, borderRadius: '50%', background: 'radial-gradient(circle at 40% 36%,#8fb0d0 0 5%,#1f2d3c 9% 30%,#05070a 34% 62%,#2c2c2c 64%)' },
  ],
  instant: [
    { left: 34, top: 8, width: 132, height: 112, borderRadius: '18px', background: 'linear-gradient(#f7f3ec,#ddd5c8)', boxShadow: '0 10px 18px #0003' },
    { left: 34, top: 92, width: 132, height: 6, background: 'linear-gradient(90deg,#d9534f 0 20%,#f0ad4e 20% 40%,#f7dc6f 40% 60%,#5cb85c 60% 80%,#337ab7 80%)' },
    { left: 128, top: 18, width: 28, height: 18, borderRadius: '4px', background: 'linear-gradient(135deg,#fafafa,#bdbdbd)', border: '2px solid #cfc6b8' },
    { left: 44, top: 18, width: 16, height: 16, borderRadius: '4px', background: '#2b2b2b' },
    { left: 68, top: 30, width: 64, height: 64, borderRadius: '50%', background: '#2a2a2a', boxShadow: '0 4px 10px #0004' },
    { left: 78, top: 40, width: 44, height: 44, borderRadius: '50%', background: 'radial-gradient(circle at 40% 36%,#a8c4dc 0 6%,#26384a 10% 34%,#05070a 38%)' },
  ],
  lens: [
    { left: 30, top: 40, width: 18, height: 50, borderRadius: '4px', background: 'linear-gradient(#bdbdbd,#6e6e6e)' },
    { left: 46, top: 30, width: 112, height: 70, borderRadius: '6px', background: 'linear-gradient(#3a3a3a,#161616 50%,#0c0c0c)', boxShadow: '0 10px 18px #0004' },
    { left: 66, top: 30, width: 40, height: 70, background: 'repeating-linear-gradient(90deg,#0f0f0f 0 2px,#262626 2px 4px)' },
    { left: 116, top: 30, width: 4, height: 70, background: '#b33' },
    { left: 156, top: 22, width: 16, height: 86, borderRadius: '6px', background: 'linear-gradient(#444,#111)' },
  ],
};

const CANVAS_WIDTH = 200;
const CANVAS_HEIGHT = 130;

export interface PresetCameraProps {
  kind: PresetCameraKind;
  /** Rendered width in px. The 200x130 canvas scales to fit. */
  width?: number;
  /** Degrees of rotation, e.g. -6 for the Studio hero. */
  rotate?: number;
}

/**
 * Scales the fixed 200x130 canvas with a transform rather than recomputing
 * every part, so the drawing keeps its proportions at any slot size.
 */
const PresetCamera: React.FC<PresetCameraProps> = ({ kind, width = 160, rotate = 0 }) => {
  const scale = width / CANVAS_WIDTH;

  return (
    <div
      aria-hidden
      style={{
        width,
        height: CANVAS_HEIGHT * scale,
        flexShrink: 0,
        position: 'relative',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          transform: `scale(${scale})${rotate ? ` rotate(${rotate}deg)` : ''}`,
          transformOrigin: 'top left',
        }}
      >
        {DRAWINGS[kind].map((part, index) => (
          <div key={index} style={{ position: 'absolute', ...part }} />
        ))}
      </div>
    </div>
  );
};

export default PresetCamera;
